/**
 * Canonical five-group roster navigation authority.
 *
 * Both navigation rows consume this file. Routes may remain reachable without
 * occupying permanent navigation; `activeFor` keeps their owning task group
 * visible for deep links and contextual entry points.
 */

import type { SubPageTab } from '@statura/ui';
import type { ProgramTag } from '@/lib/navigation/nav-v2';
import type { Mode } from '@/lib/mode/detect';

export interface RosterSectionSegment {
  id: string;
  label: string;
  href: string;
  /** Operating-mode metadata only; programmes govern presentation. */
  modes?: readonly Mode[];
  programs?: readonly ProgramTag[];
  featureFlag?: 'shift_swaps';
}

export const ROSTER_PRIMARY_TABS = [
  {
    id: 'schedule',
    label: 'Schedule',
    href: '/roster/dispatch',
    activeFor: [
      '/roster/board',
      '/roster/grid',
      '/roster/optimise-day',
      '/roster/runs',
      // Exact-path claim for the folded /roster root (307 → the board). Every
      // real roster page carries a longer claim, so this only catches the
      // bare path in longest-prefix resolution.
      '/roster',
    ],
  },
  {
    id: 'cover',
    label: 'Cover & attendance',
    href: '/roster/open-shifts',
    activeFor: [
      '/roster/open-shifts',
      '/roster/replace',
      '/roster/live',
      '/roster/runtime',
      '/roster/handovers',
    ],
  },
  {
    id: 'plan',
    label: 'Patterns & periods',
    href: '/roster/periods',
    activeFor: [
      '/home-care/schedules',
      '/roster/import',
      '/roster/templates',
      '/roster/wing-staffing',
      '/roster/forecast',
      '/roster/optimiser',
      '/roster/proposals',
      '/roster/health',
      '/roster/continuity',
    ],
  },
  {
    id: 'requests',
    label: 'Leave & availability',
    href: '/roster/leave',
    activeFor: ['/roster/shift-swaps', '/workforce/availability'],
  },
  {
    id: 'approvals',
    label: 'Time & closeout',
    href: '/roster/timesheets',
    activeFor: [
      '/roster/recalls',
      '/roster/sleepover-disturbances',
      '/roster/timesheets/reconciliation',
      '/roster/unfunded-visits',
      '/roster/time-review',
      '/roster/visit-exceptions',
      '/roster/today',
      '/roster/confirm-day',
    ],
  },
] as const satisfies ReadonlyArray<SubPageTab>;

export type RosterPrimaryGroupId = (typeof ROSTER_PRIMARY_TABS)[number]['id'];

export const ROSTER_SECTION_SEGMENTS: Readonly<
  Record<RosterPrimaryGroupId, ReadonlyArray<RosterSectionSegment>>
> = {
  schedule: [
    { id: 'dispatch', label: 'Roster board', href: '/roster/dispatch' },
    {
      id: 'visit-runs',
      label: 'Visit runs',
      href: '/roster/runs',
      modes: ['HOME_CARE', 'MIXED'],
      programs: ['SAH'],
    },
  ],
  cover: [
    { id: 'open-shifts', label: 'Open shifts', href: '/roster/open-shifts' },
    { id: 'handovers', label: 'Handovers', href: '/roster/handovers' },
  ],
  plan: [
    { id: 'periods', label: 'Periods', href: '/roster/periods' },
    { id: 'provider-import', label: 'Provider import', href: '/roster/import' },
    { id: 'templates', label: 'Master roster', href: '/roster/templates' },
    {
      id: 'recurring-schedules',
      label: 'Recurring schedules',
      href: '/home-care/schedules',
      programs: ['SAH'],
    },
    {
      id: 'wing-staffing',
      label: 'Staffing demand',
      href: '/roster/wing-staffing',
      modes: ['RESIDENTIAL', 'MIXED'],
      programs: ['RAC'],
    },
    // ONE DOOR (2026-08-18): previously reachable only from the retired
    // /roster landing page's home-care chip nav — a nav segment is now the
    // discoverable path.
    {
      id: 'continuity',
      label: 'Client continuity',
      href: '/roster/continuity',
      modes: ['HOME_CARE', 'MIXED'],
      programs: ['SAH'],
    },
    // D9 (2026-08-25) sold /roster/health. Selling a surface and leaving it
    // undiscoverable is the same defect in a different place: before this it
    // had no nav entry at all, and its only inbound for a residential
    // coordinator was via Staffing demand — which is not where anyone looks
    // for AN-ACC reassessments or a worker rostered while on approved leave.
    // No `modes`: the hub is deliberately cohort-split (showHC / showRAC), so
    // it carries real work for home care and residential alike.
    { id: 'health', label: 'Roster health', href: '/roster/health' },
  ],
  requests: [
    { id: 'leave', label: 'Leave', href: '/roster/leave' },
    { id: 'availability', label: 'Availability', href: '/workforce/availability' },
    {
      id: 'swaps',
      label: 'Swaps',
      href: '/roster/shift-swaps',
      featureFlag: 'shift_swaps',
    },
  ],
  approvals: [
    { id: 'timesheets', label: 'Timesheets', href: '/roster/timesheets' },
    { id: 'recalls', label: 'Recall to work', href: '/roster/recalls' },
    {
      id: 'sleepover-disturbances',
      label: 'Sleepover disturbances',
      href: '/roster/sleepover-disturbances',
    },
    // ONE DOOR (2026-08-18): the Today command centre's payroll sections
    // relocated to real pages — time review (day ops + Confirm the day) and
    // SAH Job 1 visit exceptions.
    { id: 'time-review', label: 'Time evidence', href: '/roster/time-review' },
    {
      id: 'visit-exceptions',
      label: 'Visit exceptions',
      href: '/roster/visit-exceptions',
      modes: ['HOME_CARE', 'MIXED'],
      programs: ['SAH'],
    },
    {
      id: 'unfunded-visits',
      label: 'Unfunded visits',
      href: '/roster/unfunded-visits',
      modes: ['HOME_CARE', 'MIXED'],
      programs: ['SAH'],
    },
    {
      id: 'period-close',
      label: 'Pay period checks',
      href: '/roster/timesheets/reconciliation',
    },
  ],
};

/** Navigation ownership only; never rewrites a URL or authorises a route. */
export function rosterNavigationPath(pathname: string, search = ''): string {
  const [path = '', query] = (pathname.split('#')[0] ?? '').split('?');
  const params = new URLSearchParams(search || query);
  if (path === '/roster' || path === '/workforce/roster') {
    const section = params.get('section');
    if (section === 'time-review' || section === 'confirm-day') return '/roster/time-review';
    if (section === 'visit-exceptions') return '/roster/visit-exceptions';
    if (section === 'live-shifts' && !params.has('view')) return '/roster/live';
    return rosterNavigationPath('/roster/dispatch', params.toString());
  }
  if (path === '/payroll/timesheet' || path === '/timesheets') return '/roster/timesheets';
  // Inbound aliases retain their existing redirect implementation.
  for (const [legacy, canonical] of [
    ['/workforce/roster', '/roster/dispatch'],
    ['/workforce/handovers', '/roster/handovers'],
  ] as const) {
    if (path === legacy || path.startsWith(`${legacy}/`))
      return canonical + path.slice(legacy.length);
  }
  if (/^\/roster\/periods\/[^/]+\/close(?:\/|$)/.test(path)) {
    return '/roster/timesheets/reconciliation';
  }
  return path;
}
